/**
 * Communication Bridge Service
 *
 * Minimal iframe ↔ parent postMessage bridge.
 * Protocol: Angular sends INIT once, React sends READY / ERROR / NAVIGATE.
 */

import {
  type InitMessage,
  type IframeEventType,
  type InitPayload,
  isInitMessage,
  createEvent,
} from '@/types/communication.types';

// =============================================================================
// TYPES
// =============================================================================

export interface BridgeConfig {
  /** Target origin for postMessage security */
  targetOrigin: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Label for log prefix */
  componentId?: string;
}

type InitHandler = (payload: InitPayload) => void;

// =============================================================================
// BRIDGE IMPL
// =============================================================================

class BridgeImpl {
  private config: Required<BridgeConfig>;
  private targetWindow: Window | null = null;
  private boundMessageHandler: (event: MessageEvent) => void;
  private initHandler: InitHandler | null = null;
  private isInitialized = false;

  constructor(config: BridgeConfig) {
    this.config = {
      debug: false,
      componentId: 'Editor',
      ...config,
    };
    this.boundMessageHandler = this.handleMessage.bind(this);
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  initialize(targetWindow?: Window): void {
    if (this.isInitialized) return;

    this.targetWindow = targetWindow || window.parent;
    window.addEventListener('message', this.boundMessageHandler);
    this.isInitialized = true;
    this.log('Initialized');
  }

  destroy(): void {
    window.removeEventListener('message', this.boundMessageHandler);
    this.initHandler = null;
    this.targetWindow = null;
    this.isInitialized = false;
    this.log('Destroyed');
  }

  // ---------------------------------------------------------------------------
  // Handler registration
  // ---------------------------------------------------------------------------

  onInit(handler: InitHandler): () => void {
    this.initHandler = handler;
    return () => { this.initHandler = null; };
  }

  // ---------------------------------------------------------------------------
  // Incoming
  // ---------------------------------------------------------------------------

  private handleMessage(event: MessageEvent): void {
    if (this.config.targetOrigin !== '*' && event.origin !== this.config.targetOrigin) {
      return;
    }

    const msg = event.data;
    if (isInitMessage(msg)) {
      this.log('INIT received', msg.payload);
      this.initHandler?.(msg.payload);
      return;
    }

    this.log('Unknown message', msg);
  }

  // ---------------------------------------------------------------------------
  // Outgoing
  // ---------------------------------------------------------------------------

  sendEvent<T = unknown>(type: IframeEventType, payload?: T): void {
    const event = createEvent(type, payload);
    this.log(`Sending ${type}`, payload);
    this.postMessage(event);
  }

  private postMessage(message: unknown): void {
    if (!this.targetWindow) {
      this.log('No target window, message dropped');
      return;
    }
    try {
      this.targetWindow.postMessage(message, this.config.targetOrigin);
    } catch (error) {
      this.log('postMessage failed', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Util
  // ---------------------------------------------------------------------------

  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      const prefix = `[${this.config.componentId}Bridge]`;
      if (data !== undefined) {
        console.log(prefix, message, data);
      } else {
        console.log(prefix, message);
      }
    }
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let bridgeInstance: BridgeImpl | null = null;

function getBridge(config?: Partial<BridgeConfig>): BridgeImpl {
  if (!bridgeInstance) {
    bridgeInstance = new BridgeImpl({
      targetOrigin: config?.targetOrigin || '*',
      debug: config?.debug ?? true,
      componentId: config?.componentId ?? 'Editor',
    });
    bridgeInstance.initialize();
  }
  return bridgeInstance;
}

function resetBridge(): void {
  if (bridgeInstance) {
    bridgeInstance.destroy();
    bridgeInstance = null;
  }
}

// =============================================================================
// STATIC WRAPPER
// =============================================================================

export class CommunicationBridge {
  static on(event: IframeEventType, handler: any): void {
    if (event === 'INIT') {
      getBridge().onInit(handler);
    }
  }

  static off(event: IframeEventType, handler: any): void {
    // Placeholder for off functionality
  }

  static sendReady(): void {
    getBridge().sendEvent('READY');
  }

  static sendError(payload: any): void {
    getBridge().sendEvent('ERROR', payload);
  }

  static sendNavigate(payload: any): void {
    getBridge().sendEvent('NAVIGATE', payload);
  }

  static sendReadyToRender(): void {
    getBridge().sendEvent('READY_TO_RENDER');
  }
}

// Export instance-based API for hooks that need full control
export { getBridge, resetBridge };